import { type ReactNode } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { screenVariants, fadeVariants } from '../../lib/motion'

// ============================================
// SCREEN TRANSITION
// Wrapper for screen-level transitions
// ============================================

export interface ScreenTransitionProps {
  /** Unique key for AnimatePresence */
  screenKey: string
  /** Children to render */
  children: ReactNode
  /** Animation mode: slide (default) or fade */
  mode?: 'slide' | 'fade'
}

/**
 * ScreenTransition - Animated screen wrapper
 *
 * Wraps screen content with entrance/exit animations.
 * Respects prefers-reduced-motion preference.
 *
 * Usage:
 * ```tsx
 * <AnimatePresence mode="wait">
 *   <ScreenTransition screenKey={currentScreen}>
 *     {renderScreen()}
 *   </ScreenTransition>
 * </AnimatePresence>
 * ```
 */
export function ScreenTransition({
  screenKey,
  children,
  mode = 'slide',
}: ScreenTransitionProps) {
  const shouldReduceMotion = useReducedMotion()
  const variants = mode === 'fade' ? fadeVariants : screenVariants

  // Skip animation entirely for reduced motion
  if (shouldReduceMotion) {
    return <div key={screenKey}>{children}</div>
  }

  return (
    <motion.div
      key={screenKey}
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="h-full"
    >
      {children}
    </motion.div>
  )
}

// ============================================
// ANIMATE PRESENCE WRAPPER
// Convenience wrapper for screen switching
// ============================================

export interface ScreenAnimatorProps {
  /** Current screen key */
  currentScreen: string
  /** Screen content */
  children: ReactNode
  /** Animation mode */
  mode?: 'slide' | 'fade'
}

/**
 * ScreenAnimator - Complete screen animation wrapper
 *
 * Combines AnimatePresence with ScreenTransition for
 * easy screen switching with animations.
 *
 * Usage:
 * ```tsx
 * <ScreenAnimator currentScreen={screen}>
 *   {renderScreen()}
 * </ScreenAnimator>
 * ```
 */
export function ScreenAnimator({
  currentScreen,
  children,
  mode = 'slide',
}: ScreenAnimatorProps) {
  return (
    <AnimatePresence mode="wait">
      <ScreenTransition key={currentScreen} screenKey={currentScreen} mode={mode}>
        {children}
      </ScreenTransition>
    </AnimatePresence>
  )
}
